import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Filter } from '@df/catalog-ui/listing/filter/filter';
import { FilterAttributeOption } from '@df/catalog-ui/listing/filter/filter-attribute-option';
import { FilterAttributeRange } from '@df/catalog-ui/listing/filter/filter-attribute-range';
import { ListingSize } from '@df/catalog-ui/listing/size/listing-size';
import { IProductDataV2 } from '@df/catalog/product/product';
import { Client } from '@df/core/service/client';
import { IfSizeDirective } from '@df/ui/common/if-size.directive';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { ICmsModalService } from '@icms/client/assets/modal/icms-modal.service';
import { Subscription } from 'rxjs';
import { Product } from '../../catalog/product/product.decorator';
import { FilterPrerenderLinksComponent } from './filter/filter-prerender-links.component';
import { FilterStock } from './filter/stock/filter-stock';
import { NProductListingFiltersModal } from './product-listing-filters.modal.component';
import { ProductListing } from './product-listing.decorator';
import { ListingSizeComponent } from './size/listing-size.component';
const CHIPS_FILTER_NUMBER = 5;
@Component({
    selector: 'product-listing-tools',
    templateUrl: 'product-listing-tools.html',
    styleUrls: ['./product-listing-tools.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [FilterPrerenderLinksComponent, TranslationModule, ListingSizeComponent, CommonModule, SizeClassDirective, IfSizeDirective]
})
export class ProductListingToolsComponent implements OnInit, OnDestroy {
    /**
     * Component subscriptions
     */
    private subscriptions: Subscription = new Subscription();
    /**
     * Listing instnace
     */
    @Input()
    listing!: ProductListing<Product<IProductDataV2>>;
    /**
     * Dependencies
     */
    constructor(
        readonly client: Client,
        private cmsModalService: ICmsModalService,
        private changeDetectorRef: ChangeDetectorRef
    ) {}
    ngOnInit() {
        this.listing && this.subscriptions.add(this.listing.subscribe(() => this.changeDetectorRef.detectChanges()));
    }
    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
    showFiltersModal(filter?: Filter<Product>) {
        if (!this.listing) {
            return;
        }
        const data: NProductListingFiltersModal.IModalData = {
            listing: this.listing,
            defaultFilter: filter
        };
        this.cmsModalService.open(undefined, NProductListingFiltersModal.id, undefined, data);
    }
    getFiltersForChips() {
        return this.listing?.getFiltersWithMatches().slice(0, CHIPS_FILTER_NUMBER);
    }
    get displayFiltersButton(): boolean {
        return this.isMobileView || !!(this.listing && this.listing.getFiltersWithMatches().length > CHIPS_FILTER_NUMBER);
    }
    get isMobileView(): boolean {
        return this.client.sizeId < 2;
    }
    getFilterButtonLabel(filter: FilterAttributeOption<Product> | FilterAttributeRange<Product> | FilterStock<Product>): string {
        const label = this.getFilterLabel(filter);
        const selectedOptionsCount = this.getSelectedOptionsCount(filter);
        if (selectedOptionsCount) {
            return `${label} (${selectedOptionsCount})`;
        }
        return label;
    }
    private getFilterLabel(filter: FilterAttributeOption<Product> | FilterAttributeRange<Product> | FilterStock<Product>): string {
        return filter.attribute.data?.label || '';
    }
    get moreFiltersOptionsCount(): number {
        const chipsFilters = this.getFiltersForChips();
        if (!chipsFilters) {
            return 0;
        }
        const moreFilters = this.listing?.getFiltersWithMatches().filter(i => chipsFilters.indexOf(i) === -1);
        if (!moreFilters) {
            return 0;
        }
        return moreFilters.reduce((prev, curr) => {
            return prev + this.getSelectedOptionsCount(curr);
        }, 0);
    }
    private getSelectedOptionsCount(filter: Filter<Product>): number {
        const filterType = filter.filterType;
        if (filterType === 'attribute-option' || filterType === 'stock') {
            return (<FilterAttributeOption<Product>>filter).selectedValues.length;
        }
        return 0;
    }
    asFilterAttributeOption(filter: Filter<Product>): FilterAttributeOption<Product> {
        return filter as FilterAttributeOption<Product>;
    }

    get listingSize(): ListingSize{
        return this.listing.size as ListingSize
    }
}
